import { Injectable, Optional } from '@angular/core';
import { SyllabusQuery, TopicLevel2Query } from '@examdojo/category/v2';
import { Entitlement, EntitlementService } from '@examdojo/core/entitlement';
import { distinctUntilArrayChanged, distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { QueryEntity } from '@examdojo/state';
import { UserQuery } from '@examdojo/user';
import { combineLatest, map, of, startWith } from 'rxjs';
import { QuestionBankStoreModel } from '../data/question-bank-store.model';
import {
  buildPaginationPageNumbers,
  PaginationUIModel,
  QuestionBankAvailableFilters,
  QuestionBankFiltersFormValue,
  QuestionBankUIModel,
} from './question-bank.model';
import { INIT_QUESTION_BANK_MARKS_RANGE, QuestionBankState, QuestionBankStore } from './question-bank.store';

@Injectable({ providedIn: 'root' })
export class QuestionBankQuery extends QueryEntity<
  QuestionBankState,
  QuestionBankStoreModel,
  QuestionBankUIModel,
  'id',
  number
> {
  constructor(
    protected override store: QuestionBankStore,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly topicLevel2Query: TopicLevel2Query,
    private readonly userQuery: UserQuery,
    @Optional() private readonly entitlementService?: EntitlementService,
  ) {
    super(store);
  }

  protected override readonly getAllEntitiesTriggers$ = () => [this.topicLevel2Query.entities$, this.topicFilters$];

  protected override readonly getActiveEntityTriggers$ = (id: number) => [
    combineLatest([
      this.selectEntity(id).pipe(
        map((entity) => entity?.topic_level_02_ids || []),
        distinctUntilArrayChanged(),
      ),
      this.topicLevel2Query.entities$,
    ]).pipe(
      map(([questionTopicIds, entities]) => {
        return entities.filter((entity) => questionTopicIds.includes(entity.id));
      }),
      distinctUntilArrayChanged(),
    ),
    this.topicFilters$,
  ];

  private readonly topicFilters$ = this.selectProp('filters').pipe(
    map((filters) => filters.topicIds ?? []),
    distinctUntilDeepChanged(),
  );

  readonly hasQuestionBankAccess$ = combineLatest([
    this.entitlementService?.selectOne(Entitlement.QuestionBank) ?? of(true),
    this.userQuery.selectHasRoleOrHigher('CONTRIBUTOR').pipe(startWith(false)),
  ]).pipe(map(([hasEntitlement, isContributorOrHigher]) => hasEntitlement || isContributorOrHigher));

  readonly availableFilters$ = combineLatest([
    this.syllabusQuery.activeProgram$,
    this.selectProp('marksRange').pipe(distinctUntilDeepChanged()),
    this.hasQuestionBankAccess$,
    this.selectProp('tags').pipe(map((tags) => tags.length > 0)),
  ]).pipe(
    map(
      ([program, marksRange, hasQuestionBankEntitlement, tagsLoaded]): QuestionBankAvailableFilters => ({
        difficulty: true,
        type: program?.code === 'HSC',
        maxPoints:
          hasQuestionBankEntitlement &&
          marksRange.min !== INIT_QUESTION_BANK_MARKS_RANGE.min &&
          marksRange.max !== INIT_QUESTION_BANK_MARKS_RANGE.max,
        latestScore: hasQuestionBankEntitlement,
        lastAttempted: hasQuestionBankEntitlement,
        tags: tagsLoaded,
      }),
    ),
  );

  readonly filtersFormValue$ = combineLatest([this.selectProp('filters'), this.selectProp('marksRange')]).pipe(
    map(
      ([filters, marksRange]): QuestionBankFiltersFormValue => ({
        completed: filters.completed ?? false,
        difficulties: filters.difficulties ?? [],
        types: filters.types ?? [],
        minPoints: filters.minPoints ?? marksRange.min,
        maxPoints: filters.maxPoints ?? marksRange.max,
        minLatestScore: filters.minLatestScore ?? 0,
        maxLatestScore: filters.maxLatestScore ?? 100,
        lastAttemptDateFrom: filters.lastAttemptDateFrom ?? null,
        lastAttemptDateTo: filters.lastAttemptDateTo ?? null,
        tags: filters.tags ?? [],
      }),
    ),
    distinctUntilDeepChanged(),
  );

  override readonly toUIModelFn = (question: QuestionBankStoreModel): QuestionBankUIModel => {
    const filters = this.getProp('filters');
    const topics = (question.topic_level_02_ids ?? [])
      .map((topicId) => {
        const topic = this.topicLevel2Query.getEntity(topicId);
        return {
          id: topicId,
          code: topic?.code ?? '',
          name: topic?.name ?? '',
          isSelected: filters.topicIds?.includes(topicId) ?? false,
        };
      })
      .filter((topic) => topic.code !== '') // Some topic ids are not in the topicLevel2Query
      .sort((a, b) => {
        if (a.isSelected !== b.isSelected) {
          return a.isSelected ? -1 : 1;
        }
        return a.code.localeCompare(b.code);
      });

    return {
      ...question,
      topics,
    };
  };

  readonly selectPagination = () =>
    combineLatest([this.selectProp('pagination'), this.selectProp('total')]).pipe(
      map(([pagination, total]): PaginationUIModel => {
        const totalPages = Math.ceil(total / pagination.pageSize);
        return {
          pageSize: pagination.pageSize,
          page: pagination.page,
          total: total,
          totalPages,
          isPreviousDisabled: pagination.page <= 1,
          isNextDisabled: pagination.page >= totalPages,
          pageNumbers: buildPaginationPageNumbers(pagination.page, totalPages),
          rangeStart: (pagination.page - 1) * pagination.pageSize + 1,
          rangeEnd: Math.min(pagination.page * pagination.pageSize, total),
        };
      }),
    );
}
